import { Delta } from "@vueup/vue-quill";
import { produce, setAutoFreeze } from "immer";
import debounce from "lodash/debounce";
import omit from "lodash/omit";
import queryString from "query-string";

import { AnalyticsEvent } from "@/enums/analytics";
import { NotableType, NoteType } from "@/enums/dealActivity";
import { ScorecardType } from "@/enums/scorecards";
import { AuthType } from "@/enums/vouched";
import { getEnumKeyFromValue } from "@/helpers/common";
import {
  DEFAULT_EXPIRATION_DAYS,
  PHASES_FOR_LENDFLOW_FOR_FUNDING_APPS,
  SIGNED_DOC_FILE_TYPE,
  STATUS_DESCRIPTIONS,
  toValidApiServiceId,
} from "@/helpers/constants";
import { deepMergeProviderData, isGivenCountryCanada } from "@/helpers/deals";
import {
  formatDate,
  formatDateCustom,
  formatMoney,
  formatPayloadWithCurrency,
  formatUserFullName,
} from "@/helpers/formatting";
import {
  createUserAssignmentAction,
  createUserRemovalAction,
} from "@/helpers/userAssignment";
import i18n from "@/i18n";
import { SmartviewCategory } from "@/models/SmartviewCategory";
import applicationsApiService from "@/services/modules/applications";
import clientsApiService from "@/services/modules/clients";
import communicationService from "@/services/modules/communication";
import offerTemplateService from "@/services/modules/offerTemplate";
import workflowApiService from "@/services/modules/workflows";
import { useAnalyticsStore } from "@/stores/analytics";
import useNotesStore from "@/stores/notes";

import type { PlaidData } from "@/models/applicationData/plaid";
import type {
  ApplicationStatus,
  ApplicationStatusPayload,
  DealMode,
  DealVisibility,
  FieldSource,
  IAdvancedCfaPayload,
  IApplication,
  IApplicationMetrics,
  IApplicationTemporaryLinks,
  IApplicationsState,
  IApplicationsStateCommercialData,
  IBankAccount,
  IBankData,
  IBillingInformation,
  IBusiness,
  IBusinessLeadProfile,
  ICommercialData,
  IDealTimeline,
  IDealVisibilityUpdate,
  IDiscoveryCallAnswer,
  IDiscoveryCallQuestion,
  IEquipmentRentalApproval,
  IFilterSort,
  IFilterVisibility,
  IInformationProviderSummary,
  IOfferDeclinedInfo,
  ISendStipsToFunderPayload,
  ISmartFilter,
  ISystemPreferences,
  ITransaction,
  ITransactionPayload,
  PhoneValidationResponse,
  StipPayload,
  UtmParameterSet,
  WidgetInfo,
  WorkflowSnapshotStatus,
} from "@/models/applications";
import type { IFile, IFilePayload, IPii, IResponseMeta } from "@/models/common";
import type { ILenderPlacement, IOffer } from "@/models/funders";
import type { OfferNote, OfferTemplate } from "@/models/offers";
import type { IIndustry } from "@/models/options";
import type { IRootState } from "@/models/state";
import type { DealTelephone, IOwner, IUser } from "@/models/users";
import type { IWorkflowStatus } from "@/models/workflows";
import type { AxiosError } from "axios";
import type { ActionContext, ActionTree, GetterTree, MutationTree } from "vuex";

type editOfferMode = "save" | "update";

type OptionCallback = (id: number | null) => string | Record<string, string>;

setAutoFreeze(false);

const getDefaultState = (): IApplicationsState => {
  return {
    meta: {} as IResponseMeta,
    all: [],
    active: {} as IApplication,
    previousId: "",
    commercialDataServicesQueue: [],
    commercialDataServicesLoading: false,
    nextId: "",
    currentPhase: 0,
    placements: [],
    singlePlacement: {} as ILenderPlacement,
    offers: [],
    placementEmailPreview: "",
    offersLoading: false,
    loading: false,
    clientPreferences: { list_order: [], daily_monthly_stat_order: [] },
    filters: [],
    filtersLoading: false,
    activeFilterId: -1,
    activeSmartviewCategory: SmartviewCategory.ALL,
    metrics: {
      business_lead_profile: {} as IBusinessLeadProfile,
      deal_timeline: {} as IDealTimeline,
      information_provider_summary: {} as IInformationProviderSummary,
    },
    fundingTransactions: {},
    smartviewBuilderOpen: false,
    smartviewBuilderModel: null,
    systemPreferences: {
      middesk_irs_availability: null,
      deal_visibility: {} as DealVisibility,
    },
    bankData: { bank_accounts: [] },
    moneythumbAnalytics: null,
    clientTrackingTokens: [],
    selectedAffiliatedBusinessId: null,
    selectedAffiliatedIndividualId: null,
    additionalTrackingTokens: [],
    widgetInfo: null,
    plaidData: null,
    plaidDataLoading: false,
    moneythumbCfaAccountNumber: i18n.global.t(
      "DEALS.DEAL_PROGRESS.BANK_CONNECTION.SUMMARY"
    ),
    files: [],
    workflow_statuses: {},
    servicesOptions: null,
    validatedPhones: [],
    offerTemplates: [],
  };
};

const state = getDefaultState();

const mutations: MutationTree<IApplicationsState> = {
  addOwner(state, payload: IOwner) {
    if (payload.is_primary) {
      if (state.active.personal_information) {
        state.active.personal_information.is_primary = false;
        state.active.business.other_owners = [
          ...state.active.business.other_owners,
          state.active.personal_information,
        ];
        state.active.personal_information = payload;
      }
    } else {
      state.active.business.other_owners = [
        ...state.active.business.other_owners,
        payload,
      ];
    }
  },
  unsetOwner(state, ownerId: number) {
    const indexInOtherOwners = state.active.business.other_owners.findIndex(
      ({ id }) => id === ownerId
    );

    if (indexInOtherOwners !== -1) {
      state.active.business.other_owners.splice(indexInOtherOwners, 1);
    } else if (state.active.personal_information?.id === ownerId) {
      state.active.personal_information = null;
    }
    state.files = state.files.filter(
      ({ personal_information }) =>
        !personal_information || personal_information?.id !== ownerId
    );
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setAll(state, applications: IApplication[]) {
    state.all = applications.map((app) => ({
      ...app,
      business_name: app?.business?.business_legal_name ?? "",
      app_started: app.created_at ? formatDate(new Date(app.created_at)) : null,
      underwriter_full_name: app.underwriter
        ? formatUserFullName(app.underwriter)
        : "",
      client_underwriter_full_name: app.client_underwriter
        ? formatUserFullName(app.client_underwriter)
        : "",
      advisor_full_name: app.funding_advisor
        ? formatUserFullName(app.funding_advisor)
        : "",
      client_advisor_full_name: app.client_funding_advisor
        ? formatUserFullName(app.client_funding_advisor)
        : "",
      analyst_full_name: app.analyst ? formatUserFullName(app.analyst) : "",
      client_analyst_full_name: app.client_analyst
        ? formatUserFullName(app.client_analyst)
        : "",
    }));
  },
  setMetadata(state, meta: IResponseMeta) {
    state.meta = meta;
  },
  setActive(state, application: IApplication) {
    state.active = {
      ...state.active,
      ...application,
    };

    if (state.active?.business?.address) {
      state.active.business.address.is_primary = true;
    }
    if (state.active?.personal_information?.address) {
      state.active.personal_information.address.is_primary = true;
    }
    state.active.business?.other_owners?.forEach((owner) => {
      if (owner.address) {
        owner.address.is_primary = true;
      }
    });
  },
  setOfferTemplates(state, offerTemplates: OfferTemplate[]) {
    state.offerTemplates = offerTemplates;
  },
  setFieldSources(
    state,
    payload: { type: string; fieldSources: FieldSource[] }
  ) {
    if (state.active.field_sources) {
      state.active.field_sources[payload.type] = payload.fieldSources;
      return;
    }
    state.active.field_sources = {
      [payload.type]: payload.fieldSources,
    };
  },
  unsetActive(state) {
    state.active = {} as IApplication;
  },
  setPrevious(state, applicationId: string) {
    state.previousId = applicationId;
  },
  setNext(state, applicationId: string) {
    state.nextId = applicationId;
  },
  setActiveStatus(state, newStatus: ApplicationStatus) {
    state.active = {
      ...state.active,
      status: newStatus,
      status_description:
        state.active.workflow === 5
          ? PHASES_FOR_LENDFLOW_FOR_FUNDING_APPS[newStatus]
          : STATUS_DESCRIPTIONS[newStatus],
    };
  },
  setActiveBusinessInfo(state, businessInfo: IBusiness) {
    state.active.business = {
      ...state.active.business,
      ...businessInfo,
    };
    if (state.active.business.address) {
      state.active.business.address.is_primary = true;
    }
  },
  setCurrentPhase(state, phase: number) {
    phase > -1 ? (state.currentPhase = phase) : (state.currentPhase = 0);
  },
  unsetCurrentPhase(state) {
    state.currentPhase = 0;
  },
  setPlacements(state, placements: ILenderPlacement[]) {
    state.placements = placements;
  },
  setSinglePlacement(state, data: ILenderPlacement) {
    state.singlePlacement = data;
  },
  unsetSinglePlacement(state) {
    state.singlePlacement = {} as ILenderPlacement;
  },
  setUpdatedPlacement(state, updatedPlacement: ILenderPlacement) {
    const storePlacement = state.placements.find(
      (placement) => placement.placement_id === updatedPlacement.placement_id
    );
    if (!storePlacement) {
      return;
    }
    Object.assign(storePlacement, updatedPlacement);
  },
  setClosingStatus(state, statusId: number) {
    state.active.closing_status_id = statusId;
  },
  setOfferStatus(state, { id, status }: IOffer) {
    state.offers = produce(state.offers, (draftOffers) => {
      const offerId = draftOffers.findIndex((offer) => offer.id === id);
      if (offerId !== -1) {
        draftOffers[offerId].status = status;
      }
    });
  },
  setOffers(state, offers: IOffer[]) {
    state.offers = offers.map((offer) => ({
      ...offer,
      funder_full_name: offer?.funder?.full_name || "-",
      product_type: offer?.placement?.product.name,
      offer_product_type: offer?.product?.name,
      risk_tiers: offer?.funder?.risk_tiers ?? [],
      tracking: offer?.placement?.tracking,
      status: offer.status,
      placement_id: offer?.placement?.placement_id,
    }));
  },
  handleDeletedDocument(state, deletedId: number) {
    state.files = state.files.filter((file) => file.id !== deletedId);

    if (state.active.personal_information) {
      state.active.personal_information.signed_docs =
        state.active.personal_information.signed_docs.filter(
          (file) => file.file_id !== deletedId
        );
    }

    if (!state.active.business?.other_owners?.length) {
      return;
    }

    state.active.business.other_owners = state.active.business.other_owners.map(
      (owner) => ({
        ...owner,
        signed_docs: owner.signed_docs.filter(
          (file) => file.file_id !== deletedId
        ),
      })
    );
  },
  setUpdatedFile(state, updatedFile: IFile) {
    state.files = produce(state.files, (draftFiles) => {
      const fileIndex = draftFiles.findIndex(
        (file) => file.id === updatedFile.id
      );
      if (fileIndex === -1) {
        return;
      }
      draftFiles[fileIndex] = updatedFile;
    });
  },
  addFileStipToDeal(state, { stipId, file }: { stipId: number; file: IFile }) {
    state.active = produce(state.active, (draftActive: IApplication) => {
      const stip = draftActive.stips.find((stip) => stip.id == stipId);
      if (!stip) {
        return;
      }
      if (!stip.files) {
        stip.files = [];
      }
      stip.files.push(file);
    });
  },
  addOffer(state, offer: IOffer) {
    const normalizedOffer = {
      ...offer,
      funder_full_name: offer?.funder?.full_name || "-",
      product_type: offer?.placement?.product.name,
      offer_product_type: offer?.product?.name,
      risk_tiers: offer?.funder?.risk_tiers ?? [],
      tracking: offer?.placement?.tracking,
      status: offer.status,
      placement_id: offer?.placement?.placement_id,
    };
    state.offers = [...state.offers, normalizedOffer];
  },
  updateOffer(state, updatedOffer: IOffer) {
    state.offers = produce(state.offers, (draftOffers: IOffer[]) => {
      const index = draftOffers.findIndex(
        (offer) => offer.id === updatedOffer.id
      );
      if (index !== -1) {
        draftOffers[index] = {
          ...draftOffers[index],
          ...updatedOffer,
        };
      }
    });
  },
  addFileToStip(state, { stipId, file }: { stipId: number; file: IFile }) {
    state.offers = produce(state.offers, (draftOffers: IOffer[]) => {
      draftOffers.forEach((offer) => {
        const stip = offer.stips.find((stip) => stip.id == stipId);
        if (stip) {
          stip.files?.push(file);
        }
      });
    });
  },
  removeFileFromStip(
    state,
    { stip_id, file_id }: { stip_id: number; file_id: number }
  ) {
    state.offers = produce(state.offers, (draftOffers: IOffer[]) => {
      draftOffers.forEach((offer) => {
        const stip = offer.stips.find((stip) => stip.id == stip_id);
        if (stip && stip.files?.find((file) => file.id == file_id)) {
          stip.files.splice(
            stip.files.findIndex((file) => file.id == file_id),
            1
          );
        }
      });
    });
  },
  removeStipFileFromDeal(
    state,
    { stip_id, file_id }: { stip_id: number; file_id: number }
  ) {
    state.active = produce(state.active, (draftActive: IApplication) => {
      const stip = draftActive.stips.find((stip) => stip.id == stip_id);
      if (stip && stip.files?.find((file) => file.id == file_id)) {
        stip.files.splice(
          stip.files.findIndex((file) => file.id == file_id),
          1
        );
      }
    });
  },
  assignFundingAdvisor(
    state,
    payload: { appId: string; removeDeal: boolean; user?: IUser }
  ) {
    let updatedApplications = state.all;
    if (payload.removeDeal) {
      updatedApplications = updatedApplications.filter(
        (application) => application.id !== payload.appId
      );
      state.all = updatedApplications;
      return;
    }

    const fundingAdvisor = payload.user;
    // update application
    updatedApplications = produce(state.all, (draft: IApplication[]) => {
      const index = draft.findIndex((app) => app.id === payload.appId);
      if (index !== -1 && fundingAdvisor && fundingAdvisor.full_name) {
        draft[index].advisor_full_name = fundingAdvisor.full_name;
        draft[index].funding_advisor = fundingAdvisor;
      }
    });
    state.all = updatedApplications;
    if (state.active && fundingAdvisor) {
      state.active.funding_advisor = fundingAdvisor;
    }
  },
  assignClientFundingAdvisor(
    state,
    { appId, user }: { appId: string; user?: IUser }
  ) {
    const advisor = user;
    if (!advisor) {
      return;
    }
    state.all = produce(state.all, (draft: IApplication[]) => {
      const app = draft.find((app) => app.id === appId);
      if (app) {
        app.client_advisor_full_name = advisor.full_name;
        app.client_funding_advisor = advisor;
      }
    });
    if (state.active) {
      state.active.client_funding_advisor = advisor;
    }
  },
  removeFundingAdvisor(state, appId: string) {
    const app = state.all.find((app) => app.id === appId);
    if (app) {
      app.advisor_full_name = "";
      app.funding_advisor = null;
    }
    if (state.active) {
      state.active.funding_advisor = null;
    }
  },
  removeClientFundingAdvisor(state, appId: string) {
    const app = state.all.find((app) => app.id === appId);
    if (app) {
      app.client_advisor_full_name = "";
      app.client_funding_advisor = null;
    }
    if (state.active) {
      state.active.client_funding_advisor = null;
    }
  },
  assignUnderwriter(state, payload: { appId: string; user?: IUser }) {
    const underwriter = payload.user;
    if (!underwriter) {
      return;
    }
    state.all = produce(state.all, (draft: IApplication[]) => {
      const index = draft.findIndex((app) => app.id === payload.appId);
      if (index !== -1) {
        draft[index].underwriter_full_name = underwriter.full_name;
        draft[index].underwriter = underwriter;
      }
    });
    if (state.active) {
      state.active.underwriter = underwriter;
    }
  },
  assignClientUnderwriter(
    state,
    { appId, user }: { appId: string; user?: IUser }
  ) {
    const underwriter = user;
    if (!underwriter) {
      return;
    }
    state.all = produce(state.all, (draft: IApplication[]) => {
      const app = draft.find((app) => app.id === appId);
      if (app) {
        app.client_underwriter_full_name = underwriter.full_name;
        app.client_underwriter = underwriter;
      }
    });
    if (state.active) {
      state.active.client_underwriter = underwriter;
    }
  },
  removeUnderwriter(state, appId: string) {
    const app = state.all.find((app) => app.id === appId);
    if (app) {
      app.underwriter_full_name = "";
      app.underwriter = null;
    }
    if (state.active) {
      state.active.underwriter = null;
    }
  },
  removeClientUnderwriter(state, appId: string) {
    const app = state.all.find((app) => app.id === appId);
    if (app) {
      app.client_underwriter_full_name = "";
      app.client_underwriter = null;
    }
    if (state.active) {
      state.active.client_underwriter = null;
    }
  },
  assignAnalyst(state, payload: { appId: string; user?: IUser }) {
    const analyst = payload.user;
    if (!analyst) {
      return;
    }
    state.all = produce(state.all, (draft: IApplication[]) => {
      const index = draft.findIndex((app) => app.id === payload.appId);
      if (index !== -1 && analyst && analyst.full_name) {
        draft[index].analyst_full_name = analyst.full_name;
        draft[index].analyst = analyst;
      }
    });
    if (state.active) {
      state.active.analyst = analyst;
    }
  },
  assignClientAnalyst(state, { appId, user }: { appId: string; user?: IUser }) {
    const analyst = user;
    if (!analyst) {
      return;
    }
    state.all = produce(state.all, (draft: IApplication[]) => {
      const app = draft.find((app) => app.id === appId);
      if (app) {
        app.client_analyst_full_name = analyst.full_name;
        app.client_analyst = analyst;
      }
    });
    if (state.active) {
      state.active.client_analyst = analyst;
    }
  },
  removeAnalyst(state, appId: string) {
    const app = state.all.find((app) => app.id === appId);
    if (app) {
      app.analyst_full_name = "";
      app.analyst = null;
    }
    if (state.active) {
      state.active.analyst = null;
    }
  },
  removeClientAnalyst(state, appId: string) {
    const app = state.all.find((app) => app.id === appId);
    if (app) {
      app.client_analyst_full_name = "";
      app.client_analyst = null;
    }
    if (state.active) {
      state.active.client_analyst = null;
    }
  },
  setUpdatedDiscoveryCallAnswers(state, data: []) {
    state.active.discovery_call_answers = data;
  },
  setCommercialData(state, data: ICommercialData) {
    const {
      commercial_data,
      statuses,
      business_legal_name,
      dates,
      request_data,
    } = data;

    state.active = produce(state.active, (draft: IApplication) => {
      if (!draft.commercialData) {
        draft.commercialData = {} as IApplicationsStateCommercialData;
      }

      // Deep merge raw provider data to preserve sibling services under the same provider group.
      // E.g., fetching clear_risk_inform_business_search shouldn't wipe clear_id_confirm_business.
      if (commercial_data) {
        Object.assign(
          draft.commercialData,
          deepMergeProviderData(
            draft.commercialData,
            commercial_data as unknown as typeof draft.commercialData
          )
        );
      }

      if (statuses) {
        draft.commercialData.statuses = deepMergeProviderData(
          draft.commercialData.statuses,
          statuses
        );
      }

      if (dates) {
        draft.commercialData.dates = deepMergeProviderData(
          draft.commercialData.dates,
          dates
        );
      }

      if (request_data) {
        draft.commercialData.request_data = deepMergeProviderData(
          draft.commercialData.request_data,
          request_data
        );
      }

      draft.commercialData.business_legal_name = business_legal_name;
    });
  },
  setPersonalInfo(state, updatedOwner: IOwner) {
    if (state.active.personal_information?.id === updatedOwner.id) {
      state.active.personal_information = {
        ...state.active.personal_information,
        ...updatedOwner,
      };
      if (state.active.personal_information.address) {
        state.active.personal_information.address.is_primary = true;
      }
      return;
    }
    if (
      state.active.business.other_owners?.some(
        (owner) => owner.id === updatedOwner.id
      )
    ) {
      const index = state.active.business.other_owners.findIndex(
        (owner) => owner.id === updatedOwner.id
      );
      state.active.business.other_owners[index] = {
        ...state.active.business.other_owners[index],
        ...updatedOwner,
      };
      if (state.active.business.other_owners[index]?.address) {
        state.active.business.other_owners[index].address.is_primary = true;
      }
    }
  },
  setPiis(state, piis: IPii[]) {
    if (state.active.personal_information) {
      state.active.personal_information.piis = piis;
    }
  },
  setTaxExempt(state, tax_exempt: number) {
    state.active.business.tax_exempt = tax_exempt;
  },
  addFilesToActiveApp(state, filesData: IFile[]) {
    state.files = [...filesData, ...state.files];

    const allOwners = [
      state.active.personal_information,
      ...(state.active.business?.other_owners || []),
    ];

    filesData.forEach((file) => {
      if (file.file_type !== SIGNED_DOC_FILE_TYPE) {
        return;
      }
      const owner = allOwners.find(
        (owner) => owner?.id === file.personal_information?.id
      );
      owner?.signed_docs.push({
        file_id: file.id,
        url: file.file_url,
        product_id: file.product_id || null,
        signature_status: 2,
      });
    });
  },
  setBillingInfo(state, billingInfo: IBillingInformation) {
    state.active.billing_information = billingInfo;
  },
  updateDiscoveryState(state, { id, status }: { id: string; status: boolean }) {
    if (state.active.id === id) {
      state.active.is_discovery_complete = status;
      return;
    }

    const index = state.all.findIndex((app) => app.id === id);

    if (index === -1) {
      return;
    }

    state.all = produce(state.all, (draft) => {
      draft[index] = {
        ...draft[index],
        is_discovery_complete: status,
      };
    });
  },
  updateEquipmentShareUse(state, equipmentShareUse: IEquipmentRentalApproval) {
    state.active.equipment_rental_approval = equipmentShareUse;
  },
  setOffersLoading(state, value: boolean) {
    state.offersLoading = value;
  },
  saveClientPreferences(
    state,
    preferences: { list_order: []; daily_monthly_stat_order: [] }
  ) {
    state.clientPreferences = preferences;
  },
  setMoneythumbAnalytics(state, analytics) {
    state.moneythumbAnalytics = analytics;
  },
  setMoneythumbCfaAccountNumber(state, accountNumber) {
    state.moneythumbCfaAccountNumber = accountNumber;
  },
  setLoading(state, val: boolean) {
    state.loading = val;
  },
  setApplicationStatus(state, status: WorkflowSnapshotStatus) {
    state.active.workflow_snapshot.workflow_status = status;
  },
  updatePlacementPending(
    state,
    { id, status }: { id: ILenderPlacement["placement_id"]; status: string }
  ) {
    state.placements = produce(
      state.placements,
      (draftPlacements: ILenderPlacement[]) => {
        const placementId = draftPlacements.findIndex(
          (place) => place.placement_id === id
        );
        if (placementId === -1) {
          return;
        }
        draftPlacements[placementId].declinePending = status === "Declined";
      }
    );
  },
  incrementOfferCountOnPlacement(state, id: ILenderPlacement["placement_id"]) {
    state.placements = produce(
      state.placements,
      (draftPlacements: ILenderPlacement[]) => {
        const placementId = draftPlacements.findIndex(
          (place) => place.placement_id === id
        );
        if (placementId === -1) {
          return;
        }
        draftPlacements[placementId].offers_count =
          draftPlacements[placementId].offers_count + 1;
      }
    );
  },
  updateActiveWorkflowStage(state, stageId) {
    state.active.stage_id = stageId;
  },
  setFilters(state, payload: ISmartFilter[]) {
    state.filters = payload;
  },
  setFiltersLoading(state, val: boolean) {
    state.filtersLoading = val;
  },
  setSmartviewBuilderOpen(state, val: boolean) {
    state.smartviewBuilderOpen = val;
  },
  setSmartviewBuilderModel(state, model: ISmartFilter) {
    state.smartviewBuilderModel = model;
  },
  setActiveFilterId(state, id: number) {
    state.activeFilterId = id;
  },
  setActiveSmartviewCategory(state, category: SmartviewCategory) {
    state.activeSmartviewCategory = category;
  },
  unsetDeletedFilter(state, id: number) {
    state.filters = state.filters.filter((f) => f.id !== id);
  },
  appendNewFilter(state, filter: ISmartFilter) {
    state.filters = [...state.filters, filter];
  },
  appendUpdatedFilter(state, newFilter: ISmartFilter) {
    const filterIndex = state.filters.findIndex((f) => f.id === newFilter.id);
    if (filterIndex === -1) {
      return;
    }
    state.filters[filterIndex] = {
      ...state.filters[filterIndex],
      ...newFilter,
    };
  },
  setMetrics(state, metrics: IApplicationMetrics) {
    state.metrics = metrics;
  },
  setDealMode(state, { id, data }: { id: string; data: DealMode }) {
    // update mode on deal view
    if (state.active.id === id) {
      state.active.deal_mode = data;
      return;
    }

    // update mode on deals lost view
    const index = state.all.findIndex((app) => app.id === id);
    if (index === -1) {
      return;
    }

    state.all = produce(state.all, (draft) => {
      draft[index] = {
        ...draft[index],
        deal_mode: data,
      };
    });
  },
  setPhoneStatus(
    state,
    payload: {
      applicationId: string;
      phone: Omit<DealTelephone, "is_primary"> & { name: string };
      isBusiness: boolean;
    }
  ) {
    const updatePhoneStatus = (phone: DealTelephone) => {
      if (phone.number === payload.phone.number) {
        return {
          ...phone,
          status: payload.phone.status,
        };
      }
      return phone;
    };

    // update mode on deal view
    if (state.active.id === payload.applicationId) {
      state.active = produce(state.active, (draft) => {
        if (payload.isBusiness) {
          if (!draft.business) {
            return;
          }
          draft.business.all_telephones =
            draft.business.all_telephones.map(updatePhoneStatus);
          return;
        }
        if (!draft.personal_information) {
          return;
        }
        draft.personal_information.all_telephones =
          draft.personal_information.all_telephones.map(updatePhoneStatus);

        if (!draft.business) {
          return;
        }

        draft.business.other_owners = draft.business?.other_owners?.map(
          (owner) => {
            if (!owner.all_telephones) {
              return owner;
            }
            return {
              ...owner,
              all_telephones: owner.all_telephones?.map(updatePhoneStatus),
            };
          }
        );
      });
      return;
    }

    // update mode on deals list view
    const index = state.all.findIndex(
      (app) => app.id === payload.applicationId
    );

    if (index === -1) {
      return;
    }

    state.all = produce(state.all, (draft) => {
      if (payload.isBusiness) {
        if (!draft[index].business) {
          return;
        }
        draft[index] = {
          ...draft[index],
          business: {
            ...draft[index].business,
            all_telephones:
              draft[index].business.all_telephones.map(updatePhoneStatus),
          },
        };
        return;
      }

      if (!draft[index].personal_information) {
        return;
      }

      draft[index] = {
        ...draft[index],
        personal_information: {
          ...draft[index].personal_information,
          all_telephones:
            draft[index].personal_information.all_telephones.map(
              updatePhoneStatus
            ),
        },
      };
    });
  },
  setFundingTransactions(state, payload: Record<string, ITransaction[]>) {
    state.fundingTransactions = {
      ...state.fundingTransactions,
      ...payload,
    };
  },
  setSystemPreferences(state, data: ISystemPreferences) {
    state.systemPreferences = data;
  },
  setDealVisibilityForRole(state, data: IDealVisibilityUpdate) {
    const { role, settings } = data;
    state.systemPreferences.deal_visibility = {
      ...state.systemPreferences.deal_visibility,
      [role]: settings,
    };
  },
  setBankData(state, data: IBankData) {
    state.bankData = data;
  },
  setPlaidData(state, data: PlaidData) {
    state.plaidData = data;
  },
  setPlaidDataLoading(state, value: boolean) {
    state.plaidDataLoading = value;
  },
  setClientTrackingTokens(state, data: string[]) {
    state.clientTrackingTokens = data;
  },
  setSelectedAffiliatedBusinessId(state, id: number) {
    state.selectedAffiliatedBusinessId = id;
  },
  setSelectedAffiliatedIndividualId(state, id: number) {
    state.selectedAffiliatedIndividualId = id;
  },
  setAdditionalTrackingTokens(state, data: string[]) {
    state.additionalTrackingTokens = data;
  },
  setPrimaryScorecard(state, scorecardId: string) {
    state.active.score_card_id = scorecardId;
  },
  setPrimaryScorecardGroup(state, scorecardGroupId: string) {
    state.active.score_card_group_id = scorecardGroupId;
  },
  setWidgetInfo(state, data: WidgetInfo) {
    state.widgetInfo = data;
  },

  updateApplicationDataInList(
    state,
    payload: {
      application_id: IApplication["id"];
      path: string;
      value: Partial<IApplication>;
    }
  ) {
    state.all = produce(state.all, (draftApplications) => {
      const index = draftApplications.findIndex(
        (app) => app.id === payload.application_id
      );
      if (index === -1) {
        return;
      }
      draftApplications[index] = {
        ...draftApplications[index],
        [payload.path]: payload.value,
      };
    });
  },
  setDealsDoStateToProcessing(state, applications: string[]) {
    state.all = produce(state.all, (draftApplications) => {
      applications.forEach((application) => {
        const index = draftApplications.findIndex(
          (app) => app.id === application
        );
        if (index !== -1) {
          // set status to processing till we get a response or a page is reloaded
          const tempLogState = {
            ...(draftApplications[index].data_orchestration_log || {}),
            status: 1,
            master_status: 2,
          } as IApplication["data_orchestration_log"];
          draftApplications[index] = {
            ...draftApplications[index],
            data_orchestration_log: tempLogState,
          };
        }
      });
    });
  },
  setFiles(state, files: IFile[]) {
    state.files = files;
  },
  setWorkflowStatuses(
    state,
    {
      statuses,
      applicationId,
    }: { statuses: IWorkflowStatus[]; applicationId: string }
  ) {
    state.workflow_statuses[applicationId] = statuses;
  },

  setServicesOptions(state, data: Record<string, unknown>) {
    state.servicesOptions = data;
  },

  setValidatedPhones(state, phones: PhoneValidationResponse[]) {
    state.validatedPhones = phones;
  },

  setCommercialDataServicesLoading(state, loading: boolean) {
    state.commercialDataServicesLoading = loading;
  },

  clearCommercialDataServicesQueue(state) {
    state.commercialDataServicesQueue = [];
  },

  addToCommercialDataServicesQueue(state, serviceId: string | string[]) {
    if (Array.isArray(serviceId)) {
      state.commercialDataServicesQueue.push(...serviceId);
    } else {
      state.commercialDataServicesQueue.push(serviceId);
    }
  },
};

const actions: ActionTree<IApplicationsState, IRootState> = {
  async getAll({ commit }, payload: Record<string, unknown>) {
    commit("setLoading", true);
    try {
      const data = await applicationsApiService.getApplications(payload);
      commit("setMetadata", data.meta);
      commit("setAll", data.data);
    } catch (error) {
      // Silently ignore cancelled requests
      if ((error as { canceled?: boolean })?.canceled) {
        return;
      }
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },
  async syncActiveToAll({ state, commit }) {
    const activeDealId = state.active.id ?? "";
    if (!activeDealId) {
      return;
    }
    const dealToUpdate = state.all.find(({ id }) => id === activeDealId);
    if (!dealToUpdate) {
      return;
    }
    const {
      data: [updatedDeal],
    } = await applicationsApiService.getApplications({
      search: activeDealId,
    });
    if (updatedDeal?.id !== dealToUpdate.id) {
      return;
    }

    commit(
      "setAll",
      state.all.map((deal) => (deal.id === activeDealId ? updatedDeal : deal))
    );
  },
  async getWorkflowStatuses({ commit }, applicationId: string) {
    const statuses: IWorkflowStatus[] =
      await workflowApiService.getWorkflowStatuses(applicationId);

    commit("setWorkflowStatuses", { statuses, applicationId });
  },
  async getAllWithSameParams({ commit, state }) {
    if (state.loading) {
      return;
    }
    const params = queryString.parse(location.search, {
      arrayFormat: "bracket",
    });

    // communication logs when opened from the deals list need to update query param for application_ids
    // instead of refactoring that usage we can simply remove this param from the getApplications request
    delete params.application_ids;

    if (state.meta.current_page === 1) {
      const data = await applicationsApiService.getApplications(params);
      commit("setMetadata", data.meta);
      commit("setAll", data.data);
    }
  },
  async getApplication({ commit }, appId: string) {
    try {
      const application = await applicationsApiService.getApplication(appId);
      commit("setActive", application);
    } catch (error) {
      // Silently ignore cancelled requests
      if ((error as { canceled?: boolean })?.canceled) {
        return;
      }
      throw error;
    }
  },
  setDealHistoryStack({ state, commit }, appId: string) {
    const activeIndex = state.all.findIndex((deal) => deal.id == appId);
    if (activeIndex !== -1) {
      commit("setPrevious", state.all?.[activeIndex - 1]?.id ?? "");
    } else {
      commit("setPrevious", "");
    }
    if (activeIndex !== state.all.length - 1) {
      commit("setNext", state.all?.[activeIndex + 1]?.id ?? "");
    } else {
      commit("setNext", "");
    }
  },
  async getWorkflowOfferTemplates({ state, commit }) {
    const workflowTemplateId = state.active.workflow_template_id;
    if (!workflowTemplateId) {
      return;
    }
    const offerTemplatesResponse = await offerTemplateService.getTemplates({
      workflow_template: String(workflowTemplateId),
      enabled: 1,
    });
    commit("setOfferTemplates", offerTemplatesResponse.data);
  },
  async getFieldSources(
    { commit },
    payload: { applicationId: string; type: string }
  ) {
    const sources = await applicationsApiService.getFieldSources(
      payload.applicationId,
      payload.type
    );
    commit("setFieldSources", {
      type: payload.type,
      fieldSources: sources.data.data,
    });
  },
  async find(
    { dispatch, commit },
    { appId, fetchOffers = true }: { appId: string; fetchOffers: boolean }
  ) {
    commit("setLoading", true);
    commit("setPlacements", []);
    commit("setOffers", []);

    try {
      const data = await applicationsApiService.getApplication(appId);
      commit("setActive", data);

      if (fetchOffers) {
        await dispatch("getOffers");
      }
      dispatch("setDealHistoryStack", appId);
      return data;
    } finally {
      commit("setLoading", false);
    }
  },
  assignFundingAdvisor: createUserAssignmentAction("fundingAdvisor"),
  async duplicateDeal(
    _,
    payload: { data: Record<string, string>; id: string }
  ) {
    return await applicationsApiService.duplicateDeal(payload.id, payload.data);
  },
  async duplicateOffer(
    { dispatch },
    payload: { id: string; ignoreSnapshot?: boolean }
  ) {
    await applicationsApiService.duplicateOffer(
      payload.id,
      payload.ignoreSnapshot ?? false
    );
    dispatch("getOffers");
  },
  assignClientFundingAdvisor: createUserAssignmentAction(
    "clientFundingAdvisor"
  ),
  removeFundingAdvisor: createUserRemovalAction("fundingAdvisor"),
  removeClientFundingAdvisor: createUserRemovalAction("clientFundingAdvisor"),
  assignUnderwriter: createUserAssignmentAction("underwriter"),
  assignClientUnderwriter: createUserAssignmentAction("clientUnderwriter"),
  removeUnderwriter: createUserRemovalAction("underwriter"),
  removeClientUnderwriter: createUserRemovalAction("clientUnderwriter"),
  assignAnalyst: createUserAssignmentAction("analyst"),
  assignClientAnalyst: createUserAssignmentAction("clientAnalyst"),
  removeAnalyst: createUserRemovalAction("analyst"),
  removeClientAnalyst: createUserRemovalAction("clientAnalyst"),
  async enrichApp({ state, commit, dispatch, rootState }, payload) {
    const stageId =
      rootState.workflows.activeStage?.type === "underwriting"
        ? rootState.workflows.activeStage?.id
        : undefined;
    await applicationsApiService.enrichApp(state.active.id, payload, stageId);
    if (payload.provider) {
      commit("addToCommercialDataServicesQueue", payload.provider);
    }
    await dispatch("getCommercialDataThroughQueue", { id: state.active.id });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { commercialData, ...applicationData } =
      await applicationsApiService.getApplication(state.active.id);
    commit("setActive", applicationData);
  },
  async enrichBCApp({ state, commit, dispatch }, payload) {
    await applicationsApiService.enrichBCApp(state.active.id, payload);
    if (payload.provider) {
      commit("addToCommercialDataServicesQueue", payload.provider);
    }
    await dispatch("getCommercialDataThroughQueue", { id: state.active.id });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { commercialData, ...applicationData } =
      await applicationsApiService.getApplication(state.active.id);
    commit("setActive", applicationData);
  },
  async getValidatedPhoneNumbers({ state, commit }) {
    const results = await applicationsApiService.getValidatedPhoneNumbers(
      state.active.id
    );
    commit("setValidatedPhones", results);
  },
  async killActiveDeal(
    { dispatch, state, commit },
    payload: Record<string, string>
  ) {
    try {
      commit("setLoading", true);
      await applicationsApiService.deadApplication(state.active.id, payload);
      await dispatch("find", { appId: state.active.id });
      dispatch("syncActiveToAll");
    } finally {
      commit("setLoading", false);
    }
  },
  async sendDeadEmail({ state }) {
    return await applicationsApiService.sendDeadEmail(state.active.id);
  },
  async reviveActiveApp({ dispatch, state }) {
    await applicationsApiService.reviveApplication(state.active.id);
    await dispatch("find", { appId: state.active.id });
    dispatch("syncActiveToAll");
  },
  async updateActiveBusinessInfo(
    { commit, state },
    businessInfo: IApplication
  ) {
    const appInfo: Partial<IApplication> = {
      naics_code: businessInfo.naics_code,
      industry: businessInfo.industry,
      business_notes: businessInfo.business_notes || null,
    };

    if (businessInfo.funds_needed_time) {
      appInfo.funds_needed_time = businessInfo.funds_needed_time;
    }

    if (businessInfo.use_of_fund_id) {
      appInfo.use_of_fund_id = businessInfo.use_of_fund_id;
    }

    const updatedBusinessInfo =
      await applicationsApiService.updateBusinessInfoForDeal(
        businessInfo,
        state.active.id
      );

    const updatedAppInfo = await applicationsApiService.updateAppInfo(
      appInfo,
      state.active.id
    );

    commit("setActive", updatedAppInfo);
    commit("setActiveBusinessInfo", updatedBusinessInfo);
  },
  async updateActivePersonalInfo(
    { commit, state },
    { owner }: { owner: IOwner }
  ) {
    commit("setLoading", true);
    try {
      const data = await applicationsApiService.updatePersonalInfo(
        owner,
        state.active.id,
        owner.id
      );
      commit("setPersonalInfo", data);

      if (data.piis) {
        commit("setPiis", data.piis);
      }
    } finally {
      commit("setLoading", false);
    }
  },
  async updatePiis({ state, commit }, data: { tax_exempt: number }) {
    await applicationsApiService.updateAppPiis(state.active.id, data);
    if (data.tax_exempt) {
      commit("setTaxExempt", data.tax_exempt == 1);
    }
  },
  async updateEquipmentShareUse({ state, commit }, equipmentShareUse) {
    await applicationsApiService.updateEquipmentShareUse(
      state.active.id,
      equipmentShareUse
    );
    commit("updateEquipmentShareUse", equipmentShareUse);
  },
  async getPlacements({ state, commit }) {
    const placements = await applicationsApiService.getPlacements(
      state.active.id
    );
    commit("setPlacements", placements);
  },
  async createPlacement({ state }, data: Partial<ILenderPlacement>) {
    const { placed } = await applicationsApiService.createPlacement(
      state.active.id,
      data
    );
    return { ...data, placed } as ILenderPlacement & { placed: boolean };
  },
  async updatePlacement(
    { commit, dispatch, state },
    data: Partial<ILenderPlacement>
  ) {
    const updatedPlacement = await applicationsApiService.updatePlacement(
      state.active.id,
      data
    );
    commit("setUpdatedPlacement", updatedPlacement);
    await dispatch("getPlacements");
  },
  async updatePlacementStatus(
    { commit, dispatch },
    { applicationId, data }: { applicationId: string; data: ILenderPlacement }
  ) {
    commit("setOffersLoading", true);
    try {
      const updatedPlacement =
        await applicationsApiService.updatePlacementStatus(applicationId, data);
      commit("setUpdatedPlacement", updatedPlacement);
      await dispatch("getOffers");
    } finally {
      commit("setOffersLoading", false);
    }
  },
  async uploadPlacementFiles(
    { state, commit },
    { placementId, data }: { placementId: string; data: File[] }
  ) {
    const file = await applicationsApiService.uploadPlacementDocuments(
      state.active.id,
      placementId,
      data
    );
    commit(
      "setGlobalMessage",
      { title: "File uploaded successfully!", type: "success" },
      { root: true }
    );
    return file;
  },
  async sendPlacementFiles(
    { state, commit },
    { placementId, fileIds }: { placementId: string; fileIds: number[] }
  ) {
    const result = await applicationsApiService.sendPlacementDocuments(
      state.active.id,
      placementId,
      fileIds
    );
    commit(
      "setGlobalMessage",
      { title: "File sent successfully!", type: "success" },
      { root: true }
    );
    return result;
  },
  async uploadFile({ state, commit }, data: IFilePayload) {
    const file = await applicationsApiService.uploadApplicationFile(
      state.active.id,
      data
    );
    commit(
      "setGlobalMessage",
      { title: "File uploaded successfully!", type: "success" },
      { root: true }
    );
    if (data.offer_stip_id) {
      commit("addFileToStip", { stipId: data.offer_stip_id, file });
    } else if (data.application_stip_id) {
      commit("addFileStipToDeal", { stipId: data.application_stip_id, file });
    }
    return file;
  },
  async sendStipsToFunder(_, data: ISendStipsToFunderPayload) {
    return await applicationsApiService.sendStipsToFunder(data.offer_id, data);
  },
  async sendContractByEmail({ state }) {
    try {
      await applicationsApiService.sendContractByEmail(state.active.id);
      return true;
    } catch {
      return false;
    }
  },
  async sendFundingContract(
    { state },
    {
      applicationId,
      contractTemplateId,
    }: { applicationId?: string; contractTemplateId?: string }
  ) {
    await applicationsApiService.sendFundingContract(
      applicationId ?? state.active.id,
      contractTemplateId
    );
  },
  async updateOfferStips(
    { commit, state },
    { id, stips }: { id: string; stips: StipPayload[] }
  ) {
    const offer = await applicationsApiService.updateOfferStips(id, stips);

    const offers = state.offers.map((item) => {
      return item.id == id
        ? {
            ...item,
            ...offer,
          }
        : item;
    });

    commit("setOffers", offers);
  },
  async updateApplicationStips(
    { commit, state },
    { id, stips }: { id: string; stips: StipPayload[] }
  ) {
    const newStips = await applicationsApiService.updateApplicationStips(
      id,
      stips
    );

    commit("setActive", { ...state.active, stips: newStips });
  },
  async detachStipFile(
    { commit },
    {
      fileId,
      stipId,
      type,
    }: { fileId: string; stipId: number; type: "application" | "offer" }
  ) {
    await applicationsApiService.detachStipFile(fileId, { id: stipId, type });

    const mutation =
      type === "offer" ? "removeFileFromStip" : "removeStipFileFromDeal";

    commit(mutation, { stip_id: stipId, file_id: Number(fileId) });
  },
  async updateOfferStatus(
    { commit, dispatch },
    { id, status, reason }: { id: string; status: string; reason?: string }
  ) {
    await applicationsApiService.updateOfferStatus(id, status, reason);
    commit("setOfferStatus", { id, status });
    await dispatch("getOffers");
  },
  async updateClosingStatus({ commit }, status: string) {
    await applicationsApiService.updateClosingStatus(state.active.id, status);
    commit("setClosingStatus", status);
  },
  async getOffers({ state, commit }) {
    if (!state.active.id) {
      return;
    }
    commit("setOffersLoading", true);
    try {
      const offers = await applicationsApiService.getOffers(state.active.id);
      commit("setOffers", offers);
    } finally {
      commit("setOffersLoading", false);
    }
  },
  async sendToLoanProvider(
    { dispatch },
    {
      offerId,
      servicer,
      payload,
    }: { offerId: string; servicer: string; payload: Record<string, string> }
  ) {
    await applicationsApiService.sendToLoanProvider(offerId, servicer, payload);
    await dispatch("getOffers");
  },
  async saveOffer(
    { dispatch, commit },
    { offer, mode = "save" }: { offer: IOffer; mode: editOfferMode }
  ) {
    const offerServiceMap = {
      save: applicationsApiService.saveOffer,
      update: applicationsApiService.updateOffer,
    };
    const offerResponse = await offerServiceMap[mode](
      formatPayloadWithCurrency(offer as unknown as Record<string, string>),
      offer.application_id
    );
    dispatch("syncActiveToAll");
    commit(
      "setGlobalMessage",
      { title: "Offer saved!", type: "success" },
      { root: true }
    );
    await dispatch("getOffers");
    return offerResponse;
  },
  async saveSelfFundedOffer(
    { dispatch, commit },
    { offer, mode = "save" }: { offer: IOffer; mode: editOfferMode }
  ) {
    const offerServiceMap = {
      save: applicationsApiService.saveSelfFundedOffer,
      update: applicationsApiService.updateOffer,
    };
    const offerResponse = await offerServiceMap[mode](
      formatPayloadWithCurrency(offer as unknown as Record<string, string>),
      offer.application_id
    );
    dispatch("syncActiveToAll");
    commit(
      "setGlobalMessage",
      { title: "Offer saved!", type: "success" },
      { root: true }
    );
    await dispatch("getOffers");
    return offerResponse;
  },
  async confirmOffer({ dispatch }, offer: Partial<IOffer>) {
    await applicationsApiService.confirmOffer(offer);
    await dispatch("getOffers");
    dispatch("syncActiveToAll");
  },
  async deleteOffer({ commit }, { offer }: { offer: IOffer }) {
    await applicationsApiService.deleteOffer(offer.application_id, offer.id);
    commit(
      "setGlobalMessage",
      { title: "Offer Deleted", type: "success" },
      { root: true }
    );
  },
  async updateContactedVia(_, payload: { offer_ids: string[]; type: string }) {
    return await applicationsApiService.updateContactedVia(
      state.active.id,
      payload
    );
  },
  async sendOffersEmail(
    { commit },
    {
      applicationId,
      offers = null,
    }: { applicationId: string; offers: string[] | null }
  ) {
    if (offers?.length) {
      offers.forEach((offerId) =>
        commit("setOfferStatus", { id: offerId, status: 2 })
      );
    }
    const responseMessage = {
      title: "Email sent successfully",
      type: "success",
    };
    try {
      await applicationsApiService.sendOffersEmail(applicationId, offers);
    } catch (error) {
      responseMessage.title =
        (
          error as AxiosError<{
            data: {
              message: string;
            };
          }>
        )?.response?.data.data.message ?? i18n.global.t("COMMON.GENERIC_ERROR");
      responseMessage.type = "error";
    } finally {
      commit("setGlobalMessage", responseMessage, { root: true });
    }
  },

  async attachOfferNote(
    { state, commit },
    { offerId, note }: { offerId: IOffer["id"]; note: OfferNote["note"] }
  ) {
    const attachedNote = await applicationsApiService.updateOfferNote(
      { note, offerId },
      state.active.id
    );
    commit("updateOffer", attachedNote);
  },

  async deleteFile({ commit }, data: { id: number }) {
    const response = await applicationsApiService.deleteFile(data.id);
    if (response.status === 204) {
      commit("handleDeletedDocument", data.id);
    }
  },
  async deleteFiles({ commit }, payload: { ids: number[] }) {
    try {
      await applicationsApiService.deleteFiles(payload);
      payload.ids.forEach((id: number) => commit("handleDeletedDocument", id));
    } catch {
      commit("setGlobalMessage", i18n.global.t("COMMON.GENERIC_ERROR"), {
        root: true,
      });
    }
  },
  async updateFile(
    { commit },
    payload: { id: number; data: Partial<IFilePayload> }
  ) {
    const data = await applicationsApiService.updateFile(
      state.active.id,
      payload.id,
      payload.data
    );
    commit("setUpdatedFile", data);
  },
  async setPrimaryScorecard(
    { commit, rootState, state },
    payload: { scorecardId: string; type: ScorecardType }
  ) {
    await applicationsApiService.setPrimaryScorecard(
      payload.scorecardId,
      state.active?.id,
      rootState.workflows.activeStage?.id,
      payload.type
    );
    const mutation =
      payload.type === ScorecardType.CARD
        ? "setPrimaryScorecard"
        : "setPrimaryScorecardGroup";
    commit(mutation, payload.scorecardId);
  },
  async updateDiscoveryCallAnswers(
    { state, commit },
    payload: IDiscoveryCallAnswer[]
  ) {
    const data = await applicationsApiService.updateDiscoveryCallAnswers(
      state.active.id,
      payload
    );
    commit("setUpdatedDiscoveryCallAnswers", data);
  },
  async addDiscoveryCallQuestion({ state }, payload: IDiscoveryCallQuestion) {
    const response = await applicationsApiService.createDiscoveryCallQuestions(
      state.active.id,
      payload
    );
    state.active.discovery_call_questions?.push(response);
  },
  async deleteDiscoveryCallQuestion({ state }, questionId: string) {
    await applicationsApiService.deleteDiscoveryCallQuestion(
      state.active.id,
      questionId
    );
    if (state.active.discovery_call_questions) {
      state.active.discovery_call_questions =
        state.active.discovery_call_questions.filter(
          (question) => question.id !== questionId
        );
    }
  },
  async getCommercialData({ commit }, payload: { id: string }) {
    try {
      const data = await applicationsApiService.getCommercialData(payload.id);
      commit("setCommercialData", data);
    } catch (error) {
      // Silently ignore cancelled requests
      if ((error as { canceled?: boolean })?.canceled) {
        return;
      }
      throw error;
    }
  },

  getCommercialDataThroughQueue: debounce(
    async (
      { commit, state }: ActionContext<IApplicationsState, IRootState>,
      payload: { id: string }
    ) => {
      const services = state.commercialDataServicesQueue.length
        ? Array.from(new Set(state.commercialDataServicesQueue))
        : undefined;

      if (!services?.length) {
        return;
      }

      // Convert service IDs to valid backend API service IDs
      // - Group-level IDs (middesk, clear, etc.) for services that support them
      // - Granular IDs (lexis_nexis_kyb_search, experian_intelliscore, etc.) for services that require them
      const validApiServiceIds = services
        ?.map((service) => toValidApiServiceId(service))
        .filter((id): id is string => Boolean(id));

      // Get unique values
      const uniqueServices = validApiServiceIds?.length
        ? Array.from(new Set(validApiServiceIds))
        : undefined;

      if (!uniqueServices?.length) {
        commit("clearCommercialDataServicesQueue");
        return;
      }

      try {
        const data = await applicationsApiService.getCommercialData(
          payload.id,
          uniqueServices
        );
        commit("setCommercialData", data);
      } finally {
        commit("setCommercialDataServicesLoading", false);
        commit("clearCommercialDataServicesQueue");
      }
    },
    300
  ),

  async generateBankAccountDocuments(
    _,
    payload: { accounts: IBankAccount[]; appId: string }
  ) {
    return await applicationsApiService.generateBankDocuments(
      payload.accounts,
      payload.appId
    );
  },
  async getResumeAppLink(
    { state },
    payload?: Partial<IApplicationTemporaryLinks>
  ) {
    return await applicationsApiService.getResumeAppLink(
      payload?.application_id ?? state.active.id,
      payload ? omit(payload, "application_id") : undefined
    );
  },

  async getLivenessLink(
    { state },
    payload?: Partial<IApplicationTemporaryLinks>
  ) {
    return await applicationsApiService.getLivenessLink(
      payload?.application_id ?? state.active.id,
      payload ? omit(payload, "application_id") : undefined
    );
  },

  async getBankStatementLink(
    { state },
    payload?: Partial<IApplicationTemporaryLinks>
  ) {
    return await applicationsApiService.getBankStatementLink(
      payload?.application_id ?? state.active.id
    );
  },

  async deleteApplication({ state }) {
    const id = state.active.id;
    if (!id) {
      return;
    }
    const response = await applicationsApiService.deleteApplication(id);
    const deletedDealIndex = state.all.findIndex((deal) => deal.id === id);
    if (deletedDealIndex !== -1) {
      state.all.splice(deletedDealIndex, 1);
    }
    return response;
  },
  async generateDocuSignLink(_, payload: Partial<IApplicationTemporaryLinks>) {
    return await applicationsApiService.generateDocuSignLink(
      payload.application_id ?? state.active.id,
      omit(payload, "application_id")
    );
  },
  async generateApplicantInfoWithDocuSignLink(
    _,
    payload: Partial<IApplicationTemporaryLinks>
  ) {
    return await applicationsApiService.generateApplicantInfoWithDocuSignLink(
      payload.application_id ?? state.active.id,
      omit(payload, "application_id")
    );
  },
  async generatePlaidLink(_, payload?: Partial<IApplicationTemporaryLinks>) {
    return await applicationsApiService.generatePlaidLink(
      payload?.application_id ?? state.active.id
    );
  },
  async generateCodatWidgetLink(
    _,
    payload?: Partial<IApplicationTemporaryLinks>
  ) {
    return await applicationsApiService.generateCodatWidgetLink(
      payload?.application_id ?? state.active.id
    );
  },
  async generateVouchedWidgetLink(
    _,
    payload?: { auth_type: AuthType; application_id?: string }
  ) {
    return await applicationsApiService.generateVouchedWidgetLink(
      payload?.application_id ?? state.active.id,
      payload?.auth_type ?? AuthType.ID
    );
  },
  async generatePersonalInfoLink(
    _,
    { ownerId, application_id }: { ownerId: number; application_id?: string }
  ) {
    return await applicationsApiService.generatePersonalInfoLink(
      application_id ?? state.active.id,
      ownerId
    );
  },
  async generateBorrowerPlatformLinkForDeal(
    _,
    {
      ownerId,
      application_id,
      expirationDays,
      borrower_platform_template_id,
      borrower_platform_template_tab_id,
    }: {
      ownerId: number;
      application_id?: string;
      expirationDays?: number;
      borrower_platform_template_id?: string;
      borrower_platform_template_tab_id?: string;
    }
  ) {
    return await applicationsApiService.generateBorrowerPlatformDealLink({
      dealId: application_id ?? state.active.id,
      ownerId,
      expirationDays: expirationDays ?? DEFAULT_EXPIRATION_DAYS,
      bpTemplateId: borrower_platform_template_id,
      bpTemplateTabId: borrower_platform_template_tab_id,
    });
  },
  async generateApplicantConsentAndBankStatementsLink(
    _,
    {
      ownerId,
      application_id,
      consent_template_id,
    }: { ownerId: number; application_id?: string; consent_template_id: string }
  ) {
    return await applicationsApiService.generateTemporaryLinksV2(
      application_id ?? state.active.id,
      ownerId,
      consent_template_id,
      { consent_bank_statements: true }
    );
  },
  async updatePrimaryTrackingToken({ state, commit }, client_token: string) {
    const updatedAppInfo = await applicationsApiService.updateAppInfo(
      {
        client_tracking_token: client_token,
      },
      state.active.id
    );
    commit("setActive", updatedAppInfo);
    return updatedAppInfo;
  },
  async addClientTrackingToken(
    { state, commit },
    data: { isPrimaryToken: boolean; client_token: string }
  ) {
    const { isPrimaryToken, client_token } = data;

    if (isPrimaryToken) {
      const payload = {
        client_tracking_token: client_token,
      };

      const updatedAppInfo = await applicationsApiService.updateAppInfo(
        payload,
        state.active.id
      );
      commit("setActive", updatedAppInfo);
      return updatedAppInfo;
    } else {
      const token = await applicationsApiService.createAdditionalTrackingToken(
        state.active.id,
        {
          token: client_token,
        }
      );

      return token;
    }
  },
  async updateAdditionalToken(
    { state },
    data: { tokenIdx: number; tokenId: number; client_token: string }
  ) {
    const { tokenId, client_token } = data;

    const token = await applicationsApiService.updateAdditionalTrackingToken(
      state.active.id,
      tokenId,
      {
        token: client_token,
      }
    );

    return token;
  },
  async getClientTrackingTokens({ commit }) {
    const { data } = await clientsApiService.getAllClientsTrackingToken();
    commit("setClientTrackingTokens", data);
  },
  async getAdditionalTrackingTokens({ commit }) {
    const { data } = await clientsApiService.getAdditionalTrackingTokens();
    commit("setAdditionalTrackingTokens", data);
  },
  async deleteAdditionalToken(_, tokenId: number) {
    await applicationsApiService.deleteAdditionalTrackingToken(
      state.active.id,
      tokenId
    );
  },
  async saveUTMParameterSet(
    { state, commit },
    payload: Omit<UtmParameterSet, "id"> & { id?: UtmParameterSet["id"] }
  ) {
    const utmParams = Object.entries(payload).reduce<
      { type: string; value: string }[]
    >((acc, [key, value]) => {
      if (key !== "id" && value) {
        acc.push({ type: key, value });
      }
      return acc;
    }, []);

    try {
      commit("setLoading", true);
      const updatedAppInfo = await applicationsApiService.updateAppInfo(
        {
          utm_parameters: utmParams,
          ...(payload.id && { utm_parameter_set_id: payload.id }),
        },
        state.active.id
      );
      commit("setActive", updatedAppInfo);
      return updatedAppInfo;
    } finally {
      commit("setLoading", false);
    }
  },
  async deleteUTMParameterSet({ state, commit }, setId: string | null) {
    try {
      commit("setLoading", true);
      const updatedAppInfo = await applicationsApiService.updateAppInfo(
        {
          utm_parameters: [],
          utm_parameter_set_id: setId,
        },
        state.active.id
      );
      commit("setActive", updatedAppInfo);
      return updatedAppInfo;
    } finally {
      commit("setLoading", false);
    }
  },
  async getAdvancedApiData(
    { commit },
    {
      applicationId,
      bookId,
      params,
    }: { applicationId: string; bookId: number; params: IAdvancedCfaPayload }
  ) {
    const response = await applicationsApiService.getAdvancedCFA(
      applicationId,
      bookId,
      params
    );
    commit("saveClientPreferences", response.meta);
    return response.data;
  },
  async getMoneythumbAnalytics(
    { commit },
    {
      applicationId,
      bookId,
      bank_accounts,
    }: { applicationId: string; bookId: number; bank_accounts?: string[] }
  ) {
    const response = await applicationsApiService.getMoneythumbAnalytics(
      applicationId,
      bookId,
      bank_accounts
    );
    commit("setMoneythumbAnalytics", response);
    return response;
  },
  async saveCfaPreference(
    _,
    {
      customerId,
      params,
    }: {
      customerId: string;
      params: Record<string, string | (string | number)[]>;
    }
  ) {
    return await applicationsApiService.saveCFAPreferences(customerId, params);
  },
  async createDocVerification(
    { state },
    payload: { data: FormData; additionalConfig?: Record<string, unknown> }
  ) {
    const additionalConfig = payload.additionalConfig || {};
    const docVerification = await applicationsApiService.createDocVerification(
      state.active.id,
      payload.data,
      additionalConfig
    );
    docVerification.report.id_class = payload.data.get("type") as string;
    state.active = {
      ...state.active,
      document_verifications: [
        docVerification,
        ...state.active.document_verifications,
      ],
    };
    return docVerification;
  },
  async updateBankAccountsVisibility({ state }, payload: IBankAccount[]) {
    await applicationsApiService.updateBankAccountsVisibility(
      state.active.id,
      payload
    );
  },
  async getFilters({ commit }) {
    commit("setFiltersLoading", true);
    try {
      const data = await applicationsApiService.getApplicationFilters();
      commit("setFilters", data);
    } finally {
      commit("setFiltersLoading", false);
    }
  },
  async deleteFilter({ commit }, id: number) {
    await applicationsApiService.deleteApplicationFilter(id);
    commit("unsetDeletedFilter", id);
  },
  async createFilter({ commit }, filter: ISmartFilter) {
    const data = await applicationsApiService.createApplicationFilter(filter);
    commit("appendNewFilter", data);
    commit("setActiveFilterId", data.id);
  },
  async updateFilter({ commit }, filter: ISmartFilter) {
    const updatedFilter = await applicationsApiService.updateApplicationFilter(
      filter.id,
      filter
    );
    commit("appendUpdatedFilter", updatedFilter);
  },
  async updateApplicationFilterSortOrder(_, data: IFilterSort) {
    await applicationsApiService.updateApplicationFilterSortOrder(data);
  },
  async updateApplicationFilterVisibility(_, data: IFilterVisibility) {
    await applicationsApiService.updateApplicationFilterVisibility(data);
  },
  async getMetrics({ commit }, appId: string) {
    const data = await applicationsApiService.getDealMetrics(appId);
    commit("setMetrics", data);
  },
  async createEquipmentRentalOffer(
    { state, commit },
    payload: Record<string, string>
  ) {
    const response = await applicationsApiService.createEquipmentRentalOffer(
      state.active.id,
      payload
    );

    commit("addOffer", response.data);
  },
  async editEquipmentRentalOffer(
    { state, commit },
    { payload, offerId }: { payload: Record<string, string>; offerId: string }
  ) {
    const response = await applicationsApiService.editEquipmentRentalOffer(
      state.active.id,
      offerId,
      payload
    );
    commit("updateOffer", response.data);
  },
  async executeDataOrchestrationTemplate(
    { rootState },
    payload: { id: string; template_id: string }
  ) {
    const { id: dealId, template_id: dataOrchestrationTemplateId } = payload;
    const stageId =
      rootState.workflows.activeStage?.type === "underwriting"
        ? rootState.workflows.activeStage?.id
        : undefined;
    await applicationsApiService.executeDataOrchestrationTemplate(
      dealId,
      dataOrchestrationTemplateId,
      stageId
    );
    const { trackEvent } = useAnalyticsStore();
    trackEvent(AnalyticsEvent.WflDataOrchestrationRun, {
      applicationId: dealId,
      dataOrchestrationId: dataOrchestrationTemplateId,
    });
  },
  async updateApplicationDealMode(
    { commit, dispatch },
    payload: { id: string; deal_mode: DealMode }
  ) {
    const response = await applicationsApiService.updateApplicationDealMode(
      payload.id,
      { deal_mode: payload.deal_mode }
    );
    dispatch("syncActiveToAll");
    commit("setDealMode", {
      id: payload.id,
      data: payload.deal_mode,
      response,
    });
  },
  async getFundingTransactions({ commit }, offerId: string) {
    const transactions =
      await applicationsApiService.getFundingTransactions(offerId);
    commit("setFundingTransactions", { [offerId]: transactions });
  },
  async getPlacementsForDeals(_, dealIds: string[]) {
    return await applicationsApiService.getPlacementsForDeals(dealIds);
  },
  async getOffersForDeals(_, dealIds: string[]) {
    return await applicationsApiService.getOffersForDeals(dealIds);
  },
  async addFundingTransaction(
    { dispatch },
    payload: {
      offerId: string;
      data: ITransactionPayload;
    }
  ) {
    const newTransaction = await applicationsApiService.addFundingTransaction(
      payload.offerId,
      payload.data
    );
    void dispatch("getFundingTransactions", payload.offerId);
    return newTransaction;
  },
  async updateFundingTransaction(
    { dispatch },
    payload: {
      offerId: string;
      transactionId: string;
      data: Partial<ITransactionPayload>;
    }
  ) {
    const { offerId, transactionId, data } = payload;
    const updatedTransaction =
      await applicationsApiService.updateFundingTransaction(
        offerId,
        transactionId,
        data
      );
    void dispatch("getFundingTransactions", offerId);
    return updatedTransaction;
  },
  async deleteFundingTransaction(
    { dispatch },
    { offerId, transactionId }: { offerId: string; transactionId: string }
  ) {
    await applicationsApiService.deleteFundingTransaction(
      offerId,
      transactionId
    );
    void dispatch("getFundingTransactions", offerId);
  },
  async getSystemPreferences({ commit }) {
    const data = await applicationsApiService.getSystemPreferences();
    commit("setSystemPreferences", data);
  },
  async setDiscoveryStatus(
    { dispatch, commit, state },
    { status, appId }: { status: number; appId?: string }
  ) {
    const id = appId || state.active.id;

    if (!id) {
      return;
    }

    await applicationsApiService.setDiscoveryStatus(id, status);
    dispatch("syncActiveToAll");
    commit("updateDiscoveryState", { id, status: Boolean(status) });
  },
  async updateApp({ state, commit }, payload: Partial<IApplication>) {
    const data = await applicationsApiService.updateAppInfo(
      payload,
      state.active.id
    );
    commit("setActive", { ...state.active, ...data });
  },
  async updateDealVisibility({ commit }, payload: IDealVisibilityUpdate) {
    await applicationsApiService.updateDealVisibility(payload);
    commit("setDealVisibilityForRole", payload);
  },

  async getBankData({ commit }, payload: string) {
    const data = await applicationsApiService.getBankData(payload);
    commit("setBankData", data);
  },
  async getWidgetInfo({ commit }, payload: string) {
    const data = await applicationsApiService.getWidgetInfo(payload);
    commit("setWidgetInfo", data);
  },
  async getPlaidData({ commit }, payload: string) {
    const data = await applicationsApiService.getPlaidData(payload);
    commit("setPlaidData", data);
  },
  async generateTaxDetailsDocuSignPlaidLink(
    _,
    payload: Partial<IApplicationTemporaryLinks>
  ) {
    const response =
      await applicationsApiService.generateTaxDetailsDocuSignPlaidLink(
        payload.application_id ?? state.active.id,
        Object.fromEntries(
          Object.entries(payload).filter(([key]) => key !== "application_id")
        )
      );
    return response.tax_details_consent_bank_statements;
  },
  async getFiles({ commit }, appId: string) {
    const data = await applicationsApiService.getFiles(appId);
    commit("setFiles", data);
  },

  async updateWorkflowStatus(
    { commit },
    {
      id,
      payload,
      isList = false,
    }: { id: string; payload: ApplicationStatusPayload; isList: boolean }
  ) {
    const workflowSnapshot = await applicationsApiService.updateWorkflowStatus(
      id,
      payload
    );

    if (isList) {
      commit("updateApplicationDataInList", {
        application_id: id,
        path: "workflow_status",
        value: workflowSnapshot.workflow_status,
      });
    } else {
      commit("setApplicationStatus", workflowSnapshot.workflow_status);
    }
  },
  async updateApplicationOwnerPhoneStatus(
    { commit, dispatch },
    payload: {
      applicationId: string;
      phone: Omit<DealTelephone, "is_primary"> & { name: string };
      shouldBlock: boolean;
      isBusiness: boolean;
    }
  ) {
    if (payload.shouldBlock) {
      await communicationService.blockPhoneNumber(omit(payload, "shouldBlock"));
    } else {
      await communicationService.unblockPhoneNumber({
        applicationId: payload.applicationId,
        phone: { number: payload.phone.number },
      });
    }
    await dispatch("syncActiveToAll");
    commit("setPhoneStatus", payload);
  },

  async getServicesOptions({ commit }) {
    const data = await applicationsApiService.getServicesOptions();
    commit("setServicesOptions", data);
  },
};

const getters: GetterTree<IApplicationsState, IRootState> = {
  all(state) {
    return state.all;
  },
  meta(state) {
    return state.meta;
  },
  active(state) {
    return state.active;
  },
  workflowStatusesByDealId: (state) => (id: string) => {
    return state.workflow_statuses?.[id] || [];
  },
  clientTrackingTokens(state) {
    return state.clientTrackingTokens;
  },
  additionalTrackingTokens(state) {
    return state.additionalTrackingTokens;
  },
  currentPhase(state) {
    return state.currentPhase;
  },
  placements(state) {
    return state.placements;
  },
  offers(state) {
    return state.offers;
  },
  offerTemplates(state) {
    return state.offerTemplates;
  },
  approvedOffers(state) {
    return (state.offers || []).filter((offer) => offer.status === 1);
  },

  offersLoading(state) {
    return state.offersLoading;
  },
  ein(state): string | undefined {
    const country = state.active.business?.address?.country?.toLowerCase();
    const piis = ["BN", "EIN"];
    const piiIndex = isGivenCountryCanada(country) ? 0 : 1;
    const pii = state.active.business?.piis?.find(
      (item) => piis[piiIndex] === item.key
    )?.value;
    return (
      pii ||
      state.active.business?.piis?.find(
        (item) => piis[piiIndex === 1 ? 0 : 1] === item.key
      )?.value
    );
  },
  ssn(state) {
    return state.active.personal_information?.piis?.find(
      (item) => item.key === "SSN"
    )?.value;
  },
  count(state) {
    return state.meta?.total;
  },
  naics_code(state) {
    return state.active.naics_code;
  },
  industry(state) {
    return state.active.industry;
  },
  business_notes(state) {
    return state.active.business_notes;
  },
  document_verifications(state) {
    return state.active.document_verifications;
  },
  field_sources(state) {
    return state.active.field_sources;
  },
  offersDeclinedInfo(state): IOfferDeclinedInfo {
    return {
      date: formatDateCustom(
        state.active.offers_declined_at || "",
        "MMM dd, yyyy"
      ),
      feedback: state.active.offers_declined_feedback || "",
    };
  },
  cfaClientPreferences(state) {
    return state.clientPreferences;
  },
  loading(state) {
    return state.loading;
  },
  singlePlacement(state) {
    return state.singlePlacement;
  },
  placementEmailContent(state, _, __, rootGetters) {
    const businessNotes = [];
    //to handle old business notes
    const { currentNotes } = useNotesStore();
    const activeBusinessNotes = currentNotes.data?.filter(
      (note) =>
        note.notable_type ===
          getEnumKeyFromValue(NotableType, NoteType.Business) &&
        Number(note.notable_id) === state.active.business.id
    );
    if (state.active.business_notes?.length) {
      businessNotes.push({ insert: state.active.business_notes });
    }
    if (!activeBusinessNotes) {
      businessNotes.push({ insert: "-" });
    } else {
      businessNotes.push({ insert: "\n" });
      activeBusinessNotes.forEach((note) => {
        if (note.visibility_scope == 1) {
          businessNotes.push({ insert: `- ${note.comment}` });
          businessNotes.push({ insert: "\n" });
        }
      });
    }

    const getIndustryType: (industry: number) => IIndustry =
      rootGetters["options/industryType"];

    const getBusinessEntityType: OptionCallback =
      rootGetters["options/businessEntityTypes"];

    const getUseOfFunds: OptionCallback = rootGetters["options/useOfFunds"];

    const industryTypeId = state.active.business?.industry_type_id;
    const businessEntityTypeId = state.active.business?.business_entity_type_id;
    const useOfFundsId = state.active.use_of_fund_id;

    const industryType = industryTypeId
      ? getIndustryType(industryTypeId)?.label
      : "-";
    const businessEntityType = businessEntityTypeId
      ? getBusinessEntityType(businessEntityTypeId)
      : "-";
    const useOfFunds = useOfFundsId ? getUseOfFunds(useOfFundsId) : "-";

    const ops = [
      { insert: "Please consider the following business for funding. \n\n" },
      { insert: "Business Legal Name: ", attributes: { bold: true } },
      { insert: state.active.business?.business_legal_name || "-" },
      { insert: "\n" },
      { insert: "Business DBA Name: ", attributes: { bold: true } },
      { insert: state.active.business?.doing_business_as || "-" },
      { insert: "\n" },
      { insert: "Business Start Date: ", attributes: { bold: true } },
      { insert: formatDateCustom(state.active.business?.business_start_date) },
      { insert: "\n" },
      { insert: "Industry: ", attributes: { bold: true } },
      { insert: industryType },
      { insert: "\n" },
      { insert: "NAICS Code: ", attributes: { bold: true } },
      {
        insert: state.active.naics_code ? String(state.active.naics_code) : "-",
      },
      { insert: "\n" },
      { insert: "Owner Name: ", attributes: { bold: true } },
      {
        insert: `${state.active.personal_information?.first_name} ${state.active.personal_information?.last_name}`,
      },
      { insert: "\n" },
      { insert: "Date of Birth: ", attributes: { bold: true } },
      {
        insert: formatDateCustom(
          state.active.personal_information?.date_of_birth
        ),
      },
      { insert: "\n" },
      { insert: "Entity Type: ", attributes: { bold: true } },
      { insert: businessEntityType ?? "-" },
      { insert: "\n" },
      { insert: "Company Address: ", attributes: { bold: true } },
      { insert: state.active.business?.address?.address_line || "-" },
      { insert: "\n" },
      { insert: "Company City: ", attributes: { bold: true } },
      { insert: state.active.business?.address?.city || "-" },
      { insert: "\n" },
      { insert: "Company State: ", attributes: { bold: true } },
      { insert: state.active.business?.address?.state || "-" },
      { insert: "\n" },
      { insert: "Company ZIP: ", attributes: { bold: true } },
      { insert: state.active.business?.address?.zip || "-" },
      { insert: "\n" },
      { insert: "Company Website: ", attributes: { bold: true } },
      { insert: state.active.business?.website_address || "-" },
      { insert: "\n" },
      { insert: "Personal Address: ", attributes: { bold: true } },
      {
        insert: state.active.personal_information?.address?.address_line || "-",
      },
      { insert: "\n" },
      { insert: "Personal City: ", attributes: { bold: true } },
      { insert: state.active.personal_information?.address?.city || "-" },
      { insert: "\n" },
      { insert: "Personal State: ", attributes: { bold: true } },
      { insert: state.active.personal_information?.address?.state || "-" },
      { insert: "\n" },
      { insert: "Personal ZIP: ", attributes: { bold: true } },
      { insert: state.active.personal_information?.address?.zip || "-" },
      { insert: "\n" },
      { insert: "Amount Requested: ", attributes: { bold: true } },
      { insert: formatMoney(state.active.prequalification?.requested_amount) },
      { insert: "\n" },
      { insert: "Monthly Revenue: ", attributes: { bold: true } },
      {
        insert:
          (state.active.business?.actual_monthly_revenue &&
            formatMoney(state.active.business?.actual_monthly_revenue)) ||
          "-",
      },
      { insert: "\n" },
      { insert: "Use of Funds: ", attributes: { bold: true } },
      { insert: useOfFunds },
      { insert: "\n" },
      { insert: "Business Notes: ", attributes: { bold: true } },
      ...businessNotes,
      { insert: "\n\n" },
      {
        insert: "Please send all approvals, offers, and/or declines to ",
      },
      {
        insert: "offers@lendflow.com",
        attributes: { link: "mailto:offers@lendflow.com" },
      },
      { insert: "." },
      { insert: "\n\n" },
    ];
    return new Delta(ops);
  },
  filters(state) {
    return state.filters;
  },
  filtersLoading(state) {
    return state.filtersLoading;
  },
  smartviewBuilderOpen(state) {
    return state.smartviewBuilderOpen;
  },
  smartviewBuilderModel(state) {
    return state.smartviewBuilderModel;
  },
  activeFilterId(state) {
    return state.activeFilterId;
  },
  activeSmartviewCategory(state) {
    return state.activeSmartviewCategory;
  },
  metrics(state) {
    return state.metrics;
  },
  fundingTransactions(state) {
    return state.fundingTransactions;
  },
  systemPreferences(state) {
    return state.systemPreferences;
  },
  previousId(state) {
    return state.previousId;
  },
  nextId(state) {
    return state.nextId;
  },
  bankData(state) {
    return state.bankData;
  },
  plaidData(state) {
    return state.plaidData;
  },
  plaidDataLoading(state) {
    return state.plaidDataLoading;
  },
  moneythumbAnalytics(state) {
    return state.moneythumbAnalytics;
  },
  selectedAffiliatedBusinessId(state) {
    return state.selectedAffiliatedBusinessId;
  },
  selectedAffiliatedIndividualId(state) {
    return state.selectedAffiliatedIndividualId;
  },
  servicesOptions(state) {
    return state.servicesOptions;
  },
  widgetInfo(state) {
    return state.widgetInfo;
  },
  moneythumbCfaAccountNumber(state) {
    return state.moneythumbCfaAccountNumber;
  },
  files(state) {
    return state.files;
  },
  validatedPhones(state) {
    return state.validatedPhones;
  },
  commercialDataServicesLoading(state) {
    return state.commercialDataServicesLoading;
  },
  commercialDataServicesQueue(state) {
    return state.commercialDataServicesQueue;
  },
};

export const applications = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
